import React, {useEffect, useState} from 'react';
import CustomButton from '../../../../components/common/CustomButton';
import {useSlackService} from '../../../../services';
import SlackSettingsSkeleton from './SlackSettingsSkeleton';
import {HiCheckCircle, HiInformationCircle, HiLink} from "react-icons/hi";
import {useLocation, useNavigate} from "react-router-dom";
import {SlackSettings as SlackSettingsType} from "../../types";
import DisconnectSlackModal from '../../../../components/Modals/DisconnectSlackModal';
import Portal from "../../../../components/Portal";
import {useSelector} from 'react-redux';
import SlackUsersSyncBanner from '../../Notifications/components/TriggerWizard/SlackUsersSyncBanner';

interface SlackSettingsProps {
  handleClose: () => void;
  handleActivationCallback?: () => void;
}

const SlackSettings: React.FC<SlackSettingsProps> = ({ handleClose, handleActivationCallback }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getSlackConnectUrl,
    disconnectSlack,
    getSlackSettings
  } = useSlackService();

  const {
    slackSettingsLoading,
    slackSettings
  } = useSelector((state: any) => state.organization);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const typedSettings = (slackSettings || null) as SlackSettingsType | null;
  const connectionStatus = !!typedSettings?.connection_status;
  const teamName = typedSettings?.team_name || '';
  const teamId = typedSettings?.team_id || '';

  useEffect(() => {
    getSlackSettings();
  }, []);

  // Poll for connection status if we just opened the OAuth URL
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isPolling && !connectionStatus) {
      interval = setInterval(() => {
        getSlackSettings(true);
      }, 3000);
    } else if (isPolling && connectionStatus) {
      setIsPolling(false);
      window.location.reload();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPolling, connectionStatus]);

  // Show error message from OAuth return (?status=error&message=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const status = params.get('status');
    const message = params.get('message');
    if (status === 'error' && message) {
      try {
        setErrorMessage(decodeURIComponent(message));
      } catch {
        setErrorMessage(message);
      }
    } else {
      setErrorMessage('');
    }
  }, [location.search]);

  const onDisconnectConfirm = async () => {
    await handleDisconnect();
    setShowDisconnectModal(false);
  }

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const url = await getSlackConnectUrl();
      if (!url) {
        throw new Error('No OAuth URL received');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      setIsPolling(true);
      handleActivationCallback?.();
    } catch (error) {
      // handled by service
    } finally {
      setIsConnecting(false);
    }
  };

  const navigateToNotifications = () => {
    navigate({ pathname: '/settings', hash: '#Notifications', search: '' });
  }

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectSlack();
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="row bg-tertiary-light dark:bg-tertiary-dark w-full h-full relative flex flex-col">
      {/* Header */}
      <div className="flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-4 !pl-6 !pt-5 dark:bg-tertiary-dark sticky top-0 z-40 shadow-lg rounded-b-2xl">
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-col min-w-0 max-w-full">
            <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter'] leading-tight truncate pr-2">
              Slack Integration
            </div>
            {/*<div*/}
            {/*  onClick={navigateToNotifications}*/}
            {/*  className="cursor-pointer flex flex-row items-center gap-2 mt-2 text-light-text-tertiary dark:text-white/60 text-sm hover:text-sky-500 dark:hover:text-sky-200 transition-colors duration-200"*/}
            {/*>*/}
            {/*  <HiLink className="w-4 h-4"/>*/}
            {/*  Manage Slack Notifications*/}
            {/*</div>*/}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
              onClick={handleClose}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      {slackSettingsLoading ? (
        <SlackSettingsSkeleton connected={false} />
      ) : (
        <div className="px-8 py-12 flex-1 overflow-y-auto">
          {!connectionStatus ? (
            <div className="space-y-6 max-w-3xl">
              {/* Main Info Card */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 shadow-sm">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 mt-2">
                    <img
                      alt="Slack"
                      className="w-12 h-12"
                      src={`https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/slack-seeklogo-2.png`}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                      Connect your Slack workspace
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                      Integrate Cadence Pro with Slack to receive real-time notifications and keep your team
                      updated on project activities, task updates, and important events directly in your Slack channels.
                    </p>
                  </div>
                </div>

                {/* Features List */}
                <div className="mb-6 pl-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    What you'll get:
                  </h3>
                  <div className="space-y-2.5">
                    <div className="flex items-start gap-3">
                      <HiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white">Real-time notifications</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">Get instant alerts for project updates, task assignments, and milestone completions</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <HiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white">Channel integration</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">Send notifications to specific Slack channels and user groups</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <HiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white">Customizable triggers</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">Configure when and what notifications to receive based on your workflow</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <HiInformationCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                        How it works
                      </p>
                      <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                        Click the button below to authorize Cadence Pro with your Slack workspace. You'll be redirected
                        to Slack to grant permissions. Once connected, you can configure notification settings from the
                        Notifications section.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Connect Button */}
                <div className="mt-6 flex justify-center">
                  <CustomButton
                    onClick={handleConnect}
                    disabled={isConnecting}
                    isLoading={isConnecting}
                    className="px-6 py-2.5 text-base"
                  >
                    {isConnecting ? 'Connecting…' : 'Connect to Slack'}
                  </CustomButton>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {errorMessage && (
                <div className="mb-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                  <div className="flex items-center gap-2">
                    <HiInformationCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-red-800 dark:text-red-300">
                        {errorMessage}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="text-red-600/70 hover:text-red-700 dark:text-red-300/70 dark:hover:text-red-200"
                      onClick={() => setErrorMessage('')}
                      aria-label="Dismiss"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <div>
                    <p className="text-sm text-green-800 dark:text-green-200 font-medium">Slack is connected</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CustomButton
                    size="sm"
                    className="px-2"
                    onClick={handleConnect}
                    isLoading={isConnecting}
                    disabled={isConnecting}
                  >
                    Reconnect
                  </CustomButton>
                  <CustomButton
                    size="sm"
                    variant="danger"
                    className="px-2"
                    onClick={() => setShowDisconnectModal(true)}
                    isLoading={isDisconnecting}
                    disabled={isDisconnecting}
                  >
                    Disconnect
                  </CustomButton>
                </div>
              </div>
              <div className="px-1">
                <p className="text-[11px] text-gray-600 dark:text-gray-400">
                  Note: Reconnect will reauthorize Slack without removing triggers. Disconnect will remove all connected Slack users and related triggers.
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-4 bg-white/90 dark:bg-gray-800/60 shadow-sm">
                <div className="text-xs text-light-text-secondary dark:text-white/70">
                  Your workspace is connected. These details help you verify the correct workspace is linked.
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-light-text-tertiary dark:text-white/60">Workspace name</span>
                    <span className="font-medium text-light-text-primary dark:text-white">{teamName || '-'} </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-light-text-tertiary dark:text-white/60">Workspace ID</span>
                    <span className="font-mono text-light-text-primary dark:text-white">{teamId || '-'}</span>
                  </div>
                </div>
              </div>
              <SlackUsersSyncBanner />
              <div className="rounded-xl border border-blue-200 dark:border-blue-800 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <HiLink className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">Next step: Configure Slack notifications</p>
                      <p className="text-xs text-blue-800 dark:text-blue-300">
                       Set up which events should notify your channels.
                      </p>
                    </div>
                  </div>
                  <CustomButton
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={navigateToNotifications}
                  >
                    Configure notifications
                  </CustomButton>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showDisconnectModal && (
        <Portal>
          <DisconnectSlackModal
            isDeleting={isDisconnecting}
            setShowModal={setShowDisconnectModal}
            handleConfirm={onDisconnectConfirm}
          />
        </Portal>
      )}

      {/* Footer */}
      <div className="px-4 py-4 items-center justify-between flex gap-4 mt-auto border-t rounded-t-md border-gray-500 dark:border-gray-700 bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg sticky bottom-0">
        <CustomButton variant="cancel" onClick={handleClose} className="ml-2">
          Close
        </CustomButton>
        {!connectionStatus && (
          <CustomButton 
            onClick={handleConnect} 
            disabled={isConnecting}
            isLoading={isConnecting}
          >
            {isConnecting ? 'Connecting…' : 'Connect'}
          </CustomButton>
        )}
      </div>
    </div>
  );
};

export default SlackSettings;